import { Button } from "@shared-ui/button";
import { useEffect, useRef } from "react";
import { Dialog } from "@/components/ui/dialog";

/**
 * The sentence, built once, so the dialog and the audit cannot disagree.
 *
 * The confirmation and the audit entry are the same claim made twice -- once to
 * the operator before the change and once to whoever asks about it later. Two
 * separate renderings of it would drift, and the drift would be invisible
 * precisely because nobody sees both at the same time.
 */
export function bulkIntentSentence(summary: PendingBulkSummary): string {
	return `${summary.label} for ${summary.count.toLocaleString("en-IN")} product${
		summary.count === 1 ? "" : "s"
	} matching ${summary.filterSummary}.`;
}

/** Everything the sentence needs, captured when the dialog opened. */
export interface PendingBulkSummary {
	/** The verb, as the operator chose it: "Publish", "Set category to Tiles". */
	label: string;
	/** How many products will be touched. */
	count: number;
	/** How many match the filter, which may exceed the cap. */
	total: number;
	excludedCount: number;
	/**
	 * The filter in words, FROZEN with the rest.
	 *
	 * It used to be passed live while the count was frozen, so a filter change
	 * under an open dialog rewrote the sentence and left the ids alone: the
	 * dialog then described a scope its own product list had never come from.
	 */
	filterSummary: string;
	/**
	 * Whether this change can be refused per product. Only a publish runs the
	 * gate -- `bulkUpdateProducts` collects blockers only for rows that will end
	 * up active -- so promising a refusal list for a tier change describes an
	 * outcome that cannot happen.
	 */
	mayRefuse: boolean;
	/**
	 * WHEN `count` was taken, so the dialog can say so.
	 *
	 * Resolving a wide selection is up to five sequential requests, and the
	 * number freezes the moment the last one lands. Nothing else on this dialog
	 * moves afterwards -- that is the whole point of freezing it -- so one left
	 * open while an operator thinks reads exactly like one opened a second ago.
	 * The timestamp is the only thing that can tell them apart.
	 */
	resolvedAt: Date;
}

/**
 * The resolve time, to the minute.
 *
 * `hour12: false` and a fixed two-digit shape: the line sits next to a count in
 * `en-IN` grouping, and a clock that renders "2:05 pm" in one environment and
 * "14:05" in another is one more thing that differs between what an operator
 * sees and what a screenshot of it shows.
 */
function formatResolvedAt(at: Date): string {
	return at.toLocaleTimeString("en-IN", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});
}

/**
 * What a bulk action is about to do, in one sentence, before it does it.
 *
 * THE SENTENCE IS THE POINT. `products-dashboard.tsx` argues that a selection
 * the operator cannot see is "the shape of an accident nobody can review
 * afterwards, because the audit records the change and not the intent". Acting
 * on 500 products across twenty pages is exactly that shape unless the intent is
 * stated and read first.
 *
 * EVERYTHING IN THE SENTENCE IS FROZEN, count and filter alike, at the moment
 * the dialog opens. That is the number and the scope the operator read before
 * deciding, and neither may move underneath them.
 *
 * OVER THE CAP IT REFUSES rather than truncating. "Act on 500 of your 1,204" is
 * a decision nobody made; saying how many matched and asking for a narrower
 * filter is one they can make.
 */
export function BulkConfirmDialog({
	open,
	pending,
	summary,
	cap,
	onConfirm,
	onCancel,
}: {
	open: boolean;
	pending?: boolean;
	summary: PendingBulkSummary | null;
	cap: number;
	onConfirm: () => void;
	onCancel: () => void;
}) {
	const confirmRef = useRef<HTMLButtonElement>(null);

	/*
	 * Focus starts on CONFIRM rather than on the first control.
	 *
	 * `Dialog` focuses the first focusable thing, which here is Cancel -- right
	 * for a form, wrong for a confirmation, where the button an operator came to
	 * press is the second one. This nudges it once the shell has opened; the
	 * trap, Escape and the focus restore are the shell's.
	 */
	useEffect(() => {
		if (open) confirmRef.current?.focus();
	}, [open]);

	if (!open || !summary) return null;

	// `count` is deliberately absent: the sentence carries it, built by
	// `bulkIntentSentence` so this dialog and the audit row cannot word it
	// differently.
	const { label, total, excludedCount, filterSummary, mayRefuse, resolvedAt } =
		summary;
	const overCap = total > cap;

	return (
		<Dialog open titleId="bulk-confirm-title" onClose={onCancel}>
			<h2
				id="bulk-confirm-title"
				// The refusal is an alert: it answers something the operator asked
				// for, and it is the branch with no button to carry focus.
				role={overCap ? "alert" : undefined}
				className="text-lg font-semibold"
			>
				{overCap ? "Too many products" : "Confirm this change"}
			</h2>

			{overCap ? (
				<>
					<p className="text-sm text-slate-700">
						{total.toLocaleString("en-IN")} products match {filterSummary}, and
						a single bulk action is limited to {cap}.
					</p>
					<p className="text-sm text-slate-700">
						Narrow the filter and try again. Unticking rows will not help -- the
						limit is on how many the filter matches, not on how many of them you
						keep.
					</p>
				</>
			) : (
				<>
					{/*
					 * Verb, count and filter -- the three things that make this
					 * reviewable. "Publish 342 products" without the filter is not a
					 * sentence anyone can check afterwards.
					 *
					 * Built by the SAME function that produces the audit's intent
					 * string, with the verb emphasised here. Writing it twice -- once
					 * in markup and once in a template -- is how the confirmation and
					 * the record quietly stop matching, and nobody sees both at once.
					 */}
					<p className="text-sm text-slate-700">
						<strong>{label}</strong>
						{bulkIntentSentence(summary).slice(label.length)}
					</p>
					{/*
					 * WHEN the number was taken, beneath the number itself.
					 *
					 * Everything above this line is frozen and none of it looks
					 * frozen. An operator who opened this, went to check something
					 * and came back has no way to tell a count taken seconds ago
					 * from one taken ten minutes ago -- and over that gap a
					 * colleague publishing from another tab is enough to make it
					 * wrong.
					 */}
					<p className="text-xs text-slate-500">
						Counted at {formatResolvedAt(resolvedAt)}.
					</p>
					{excludedCount > 0 ? (
						<p className="text-sm text-slate-700">
							{excludedCount} product{excludedCount === 1 ? "" : "s"} you
							unticked {excludedCount === 1 ? "is" : "are"} excluded.
						</p>
					) : null}
					{mayRefuse ? (
						/*
						 * Only for a publish, and it no longer promises the batch
						 * survives. The old line said refusals arrive "rather than
						 * failing the whole action", which was false twice over: no
						 * other field runs the gate at all, and a product deleted
						 * between the resolve and the request makes the whole request
						 * 404.
						 */
						<p className="text-xs text-slate-500">
							Products the publish gate refuses are listed afterwards with the
							reason.
						</p>
					) : null}
				</>
			)}

			<div className="flex justify-end gap-2 pt-1">
				<Button variant="ghost" onClick={onCancel} disabled={pending}>
					Cancel
				</Button>
				{overCap ? null : (
					<Button ref={confirmRef} onClick={onConfirm} disabled={pending}>
						{pending ? "Applying…" : label}
					</Button>
				)}
			</div>
		</Dialog>
	);
}
